/**
 * Unread comments: which ones a member has not read yet, and how far a mark-read moves them.
 * Pure — no clock, no database. The handlers pass `now` in, and the list queries in SQL
 * mirror `isUnread` (KTD8). Change one and the other must follow.
 *
 * Every stamp is the 24-character `toISOString()` shape `Stamp.now()` writes. In that shape,
 * string order is time order, so the comparisons here are plain string comparisons, the same
 * ones SQLite makes on the TEXT columns (`migrations/0007_material_reads.sql`).
 */

const ISO_STAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;

/**
 * Whether a value is a stamp in the `toISOString()` shape. A stamp the parser would round to
 * another day (`2026-02-30`) fails, because string order would then lie about time order.
 * The mark-read body parser reuses this.
 */
export const isIsoStamp = (value: unknown): value is string => {
  if (typeof value !== 'string' || !ISO_STAMP.test(value)) return false;
  const ms = Date.parse(value);
  return !Number.isNaN(ms) && new Date(ms).toISOString() === value;
};

export interface UnreadInput {
  readonly authorId: string;
  readonly deleted: boolean;
  readonly createdAt: string;
  readonly viewerId: string;
  /** `group_members.read_floor_at`. Null on rows an older Worker wrote; `joinedAt` stands in. */
  readonly readFloorAt: string | null;
  readonly joinedAt: string;
  /** `material_reads.last_read_at`. Null until the member's first mark-read. */
  readonly lastReadAt: string | null;
}

/**
 * One comment's unread status for one member (R1–R4). The bound is the later of the floor
 * and the watermark. The comparison is strict, so a comment stamped exactly at the bound is
 * read. Own and deleted comments never count; a reply under a deleted root is its own row
 * and still does.
 */
export const isUnread = (c: UnreadInput): boolean => {
  if (c.authorId === c.viewerId || c.deleted) return false;
  const floor = c.readFloorAt ?? c.joinedAt;
  const bound = c.lastReadAt !== null && c.lastReadAt > floor ? c.lastReadAt : floor;
  return c.createdAt > bound;
};

/**
 * The watermark after a mark-read (R9, KTD4): the greater of the stored one and the lesser
 * of what the client sent and `now`. A stale tab cannot move it back. A skewed client clock
 * cannot move it past the present. A malformed `sent` changes nothing, so the result is
 * null only when nothing was stored and nothing usable arrived.
 */
export const nextWatermark = (existing: string | null, sent: string, now: string): string | null => {
  if (!isIsoStamp(sent)) return existing;
  const clamped = sent < now ? sent : now;
  return existing !== null && existing >= clamped ? existing : clamped;
};

export interface FrontierComment {
  readonly id: string;
  readonly authorUserId: string;
  readonly deleted: boolean;
  readonly createdAt: string;
}

/**
 * What a mark-read may send (R9): the createdAt of the last comment in an unbroken run of
 * seen ones, oldest first. A thread renders replies under their parent, so screen order is
 * not time order, and the newest comment on screen can be newer than one still below the
 * fold. The run stops at the first colleague comment that has not been on screen; the
 * viewer's own and deleted comments never stop it, because they are never unread. Ties in
 * `createdAt` break on `id`, so the order is total. Null when nothing before the gap counts.
 */
export const readFrontier = (
  comments: readonly FrontierComment[],
  seenIds: ReadonlySet<string>,
  viewerId: string | null,
): string | null => {
  const ordered = [...comments].sort((a, b) =>
    (a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  let frontier: string | null = null;
  for (const comment of ordered) {
    const seen = seenIds.has(comment.id) || comment.deleted || comment.authorUserId === viewerId;
    if (!seen) break;
    frontier = comment.createdAt;
  }
  return frontier;
};
